import { Field, Float, Int, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../base/BaseEntity'
import { RelatedEntity } from '../base/RelatedEntity'
import { UnitOfMeasure } from '../unitOfMeasure/UnitOfMeasure'

/**
 * LowProductInventory entity - cached representation of products below minimum quantity threshold
 *
 * This entity stores pre-computed low inventory data to avoid expensive calculations
 * on every query. Data is refreshed automatically when stale (24h) or manually via
 * the invalidateCache option.
 *
 * Only includes fields that are:
 * - Needed for display in the low inventory list
 * - Used for filtering/sorting
 * - Computed values that are expensive to calculate
 */
@ObjectType()
@modelOptions({ schemaOptions: { timestamps: true, collection: 'lowproductinventories' } })
export class LowProductInventory extends BaseEntity {
  // Fields from BaseEntity: _id, code, name, status, remarks, tenant

  /**
   * Whether this product is for sale
   */
  @Field(() => Boolean)
  @Prop({ type: Boolean })
  forSale: boolean

  /**
   * Minimum quantity allowance threshold
   */
  @Field(() => Float)
  @Prop({ type: Number })
  minimumQuantityAllowance: number

  /**
   * Current computed quantity on hand (includes BOM calculations if applicable)
   */
  @Field(() => Float)
  @Prop({ type: Number })
  computedQuantityOnHand: number

  /**
   * Required quantity to meet minimum threshold
   */
  @Field(() => Float)
  @Prop({ type: Number })
  requiredQuantity: number

  /**
   * Indicates if this product has sub-components (Bill of Materials)
   */
  @Field(() => Boolean)
  @Prop({ type: Boolean })
  hasSubComponents: boolean

  /**
   * Timestamp when this data was last computed/refreshed
   * Used for staleness detection
   */
  @Field(() => Date)
  @Prop({ type: Date })
  lastComputedAt: Date

  /**
   * Base unit of measure for this product
   */
  @Field(() => UnitOfMeasure, { nullable: true })
  @Prop({ type: RelatedEntity })
  baseUom?: ReturnType<() => UnitOfMeasure>

  /**
   * Decimal precision for unit of measure display
   */
  @Field(() => Int, { nullable: true })
  @Prop({ type: Number })
  unitOfMeasurePrecision?: number
}
